import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { formatRp, parseRupiah, formatRupiahInput } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const input = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]";

// Admin/super_admin: apply a discount (nominal Rp or %) and/or force payment status.
export default function PaymentAdjustDialog({ reservation, open, onClose, onDone }) {
  const r = reservation || {};
  const hargaAwal = Number(r.harga_awal || r.total_tagihan || r.harga_final || 0);

  const [dtype, setDtype] = useState("none");
  const [dval, setDval] = useState("");
  const [setStatus, setSetStatus] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDtype(r.diskon_type || "none");
      setDval(r.diskon_type === "nominal" ? formatRupiahInput(r.diskon_value) : r.diskon_type === "persen" ? String(r.diskon_value || "") : "");
      setSetStatus("");
      setReason("");
    }
    // eslint-disable-next-line
  }, [open, r.id]);

  const numVal = dtype === "nominal" ? parseRupiah(dval) : Number(dval || 0);
  const diskon = dtype === "nominal" ? Math.min(numVal, hargaAwal)
    : dtype === "persen" ? Math.round((hargaAwal * Math.min(numVal, 100)) / 100) : 0;
  const totalAkhir = Math.max(0, hargaAwal - diskon);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        discount_type: dtype,
        discount_value: dtype === "nominal" ? parseRupiah(dval) : Number(dval || 0),
        set_status: setStatus || null,
        reason,
      };
      await api.post(`/payments/reservation/${r.id}/adjust`, payload);
      toast.success("Pembayaran diperbarui");
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="payment-adjust-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Edit Pembayaran · {r.nama_lengkap}</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <div className="rounded-xl border border-[#E0D2FF] bg-[#FAF7FF] p-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Harga Awal</span><span data-testid="adjust-harga-awal" className="font-bold text-slate-800">{formatRp(hargaAwal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Diskon</span><span data-testid="adjust-diskon" className="font-semibold text-rose-600">− {formatRp(diskon)}</span></div>
            <div className="mt-1 flex justify-between border-t border-[#E0D2FF] pt-1"><span className="font-semibold text-slate-600">Total Setelah Diskon</span><span data-testid="adjust-total" className="font-extrabold text-[#7C4DE0]">{formatRp(totalAkhir)}</span></div>
            <div className="flex justify-between text-xs"><span className="text-slate-400">Total Dibayar</span><span className="text-emerald-600">{formatRp(r.total_dibayar)}</span></div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Jenis Diskon</label>
            <select data-testid="adjust-disc-type" value={dtype} onChange={(e) => { setDtype(e.target.value); setDval(""); }} className={input}>
              <option value="none">Tidak ada</option>
              <option value="nominal">Nominal (Rp)</option>
              <option value="persen">Persentase (%)</option>
            </select>
          </div>

          {dtype !== "none" && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">{dtype === "nominal" ? "Nominal Diskon (Rp)" : "Persentase Diskon (%)"}</label>
              {dtype === "nominal" ? (
                <input data-testid="adjust-disc-value" inputMode="numeric" value={dval}
                  onChange={(e) => setDval(formatRupiahInput(e.target.value))} placeholder="cth: 50.000" className={input} />
              ) : (
                <input data-testid="adjust-disc-value" type="number" min="0" max="100" value={dval}
                  onChange={(e) => setDval(e.target.value)} placeholder="cth: 10" className={input} />
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Set Status Pembayaran</label>
            <select data-testid="adjust-set-status" value={setStatus} onChange={(e) => setSetStatus(e.target.value)} className={input}>
              <option value="">Otomatis (dari pembayaran)</option>
              <option value="Belum Bayar">Belum Bayar</option>
              <option value="DP Terbayar">DP Terbayar</option>
              <option value="Lunas">Lunas</option>
            </select>
            <p className="mt-1 text-[11px] text-slate-400">Perubahan status manual dicatat sebagai penyesuaian admin di riwayat & log.</p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Alasan / Catatan</label>
            <input data-testid="adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="cth: diskon promo pelanggan setia" className={input} />
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
          <button data-testid="adjust-submit" onClick={submit} disabled={busy} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white disabled:opacity-50">Simpan</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
